import { Resolver, useForm } from 'react-hook-form';
import { paymentConfigProps } from '../types';
import { initialPaymentConfigData } from './constant';
import { useCallback, useEffect, useState } from 'react';
import { SnackbarSeverityType } from '../../../enums';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import { uploadImageToS3 } from '../../../apis/uploadToS3';
import {
  getPaymentConfigBySchool,
  upsertPaymentConfig
} from '../../../apis/paymentConfig';
import { fetchSchoolClassesConfig } from '../../../redux/slices/SchoolConfigSlice';
import { yupResolver } from '@hookform/resolvers/yup';
import { validateSchoolPaymentConfigSchema } from './validationSchema';
import formUtils from '../../../utils/formUtils';

export const usePaymentConfig = () => {
  const dispatch: AppDispatch = useDispatch();

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const { schoolClassesConfig } = useSelector(
    (state: RootState) => state.schoolConfig
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<paymentConfigProps, any, paymentConfigProps>({
    resolver:
      (yupResolver(
        validateSchoolPaymentConfigSchema
      ) as unknown as Resolver<paymentConfigProps>) || undefined,
    mode: 'onChange',
    defaultValues: {
      ...initialPaymentConfigData,
      school: loggedInUser?.school
    }
  });
  watch();
  const [expandedSection, setExpandedSection] = useState<string | false>(
    'general'
  );

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const gradeOptions = (schoolClassesConfig?.grades || []).map(
    (grade: string) => ({
      label: grade,
      value: grade
    })
  );

  const fetchPaymentConfig = useCallback(async () => {
    try {
      const response: any = await getPaymentConfigBySchool(
        loggedInUser?.school
      );
      const arrayKeysToProcess = ['gradeWiseConfig'];
      const processedPaymentConfig = formUtils.addUniqueIdsToArrayItems(
        response,
        arrayKeysToProcess
      ) as paymentConfigProps;
      reset(processedPaymentConfig);
    } catch (error: any) {
      reset();
    }
  }, [loggedInUser?.school, reset]);

  useEffect(() => {
    fetchPaymentConfig();
    dispatch(fetchSchoolClassesConfig(loggedInUser?.school));
  }, [dispatch, loggedInUser?.school, fetchPaymentConfig]);

  const handleAccordionChange = useCallback(
    (section: string) => (_: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedSection(isExpanded ? section : false);
    },
    []
  );

  const handleFileUpload = useCallback(
    async (file: File, uploadFor: string): Promise<string> => {
      if (!loggedInUser?.school) {
        throw new Error('User school information is not available.');
      }
      const url = await uploadImageToS3({
        file,
        uploadFor: uploadFor,
        fileName: file.name,
        school: loggedInUser.school
      });
      return url;
    },
    [loggedInUser?.school]
  );

  const onSubmit = useCallback(async (data: any) => {
    const featureConfigs = { ...data.featureConfigs };
    if (
      featureConfigs.enachPayment &&
      'offsetDays' in featureConfigs.enachPayment
    ) {
      const { offsetDays, ...rest } = featureConfigs.enachPayment;
      featureConfigs.enachPayment = rest;
    }

    const updatedData = {
      ...data,
      featureConfigs
    };

    try {
      const response = await upsertPaymentConfig(updatedData);
      if (response) {
        setToastOpen(true);
        setToastSeverity(SnackbarSeverityType.success);
        setToastMessage('Payment Config updated successfully!');
      }
    } catch (error: any) {
      setToastOpen(true);
      setToastSeverity(SnackbarSeverityType.error);
      setToastMessage(
        error.errors?.[0]?.message ??
          'Failed to update school. Please try again.'
      );
    }
  }, []);

  const onError = useCallback((formErrors: typeof errors) => {
    const firstErrorKey = Object.keys(formErrors)[0];
    const firstErrorMessage = (formErrors as any)[firstErrorKey]?.message;
    setToastOpen(true);
    setToastSeverity(SnackbarSeverityType.error);
    setToastMessage(
      firstErrorMessage || `Please correct the form error in ${firstErrorKey}.`
    );
  }, []);

  return {
    control,
    expandedSection,
    toastOpen,
    toastMessage,
    toastSeverity,
    userRole: loggedInUser?.role,
    gradeOptions,
    handleAccordionChange,
    handleSubmit: handleSubmit(onSubmit, onError),
    reset,
    handleFileUpload,
    setToastOpen,
    setValue
  };
};
